import { IPromise, IQService, ITimeoutService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { CommentService } from "@gtmhub/comments/services/comment.service";
import { IStateInit } from "@gtmhub/core/routing";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IFeedBadgeAssignItem, IFeedEntry, IFeedEntryComment, IFeedEntryTypeEnum } from "@gtmhub/feed/models";
import { FeedActions } from "@gtmhub/feed/redux/feed-actions";
import { IFeedStoreState } from "@gtmhub/feed/redux/feed-reducer";
import { getTargetTypeForComments } from "@gtmhub/feed/utils";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { USER_ROLES_VIEWONLY } from "@gtmhub/shared/utils/user-roles";
import { INgRedux } from "@gtmhub/state-management";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { Comment, Gif } from "@webapp/comments";
import { CommentsOrdering } from "@webapp/comments/components/comments-ordering/comments-ordering.models";
import { createErrorObject } from "@webapp/error-handling/error-util";
import { RichTextEditorComponent } from "@webapp/shared/rich-text-editor/rich-text-editor.component";

const delay = 300;

export interface IFeedCommentsScope extends IModalScope {
  params: IFeedCommentsStateParams;
  feedEntry: IFeedEntry;
  feedFetched: boolean;
  reduxItemsError: IUIError;
  mentions: string[];
  getTargetTypeForComments(string): string;
  editedCommentId: string;
  handleEditorOpen(commentId: string): void;
  setCurrentCommentMentions(currentMentions: string[]): void;
  onCommentPosted(comment: Comment): void;
  onCommentEdited(comment: Comment): void;
  updateFeedEntryComments(comments: Comment[]): void;
  createComment(args: { text: string; gif: Gif; mentions: string[]; editor: RichTextEditorComponent }): void;
  commentsSortingChanged($event: CommentsOrdering): void;
  commentsOrdering: CommentsOrdering;
  editorButtonsDisabled: boolean;
  metadata: object;
  isCurrentUserViewOnly: boolean;
}

interface IFeedCommentsStateParams {
  targetId: string;
}

const bindStateToScope = (state: IFeedStoreState) => {
  if (state.feed.selectedFeedEntry) {
    state.feed.selectedFeedEntry.displayed = true;
  }

  return {
    feedEntry: state.feed.selectedFeedEntry,
    feedFetched: state.feed.isFetched,
    reduxItemsError: state.feed.error,
  };
};

export class FeedCommentsThreadCtrl implements IStateInit {
  public static $inject = [
    "$scope",
    "$stateParams",
    "$timeout",
    "$ngRedux",
    "$q",
    "FeedActions",
    "MetricService",
    "GoalService",
    "CurrentUserService",
    "CommentService",
    "UIErrorHandlingService",
  ];

  private entityOwnersSet: Set<string>;
  private mentionsSet: Set<string>;
  private currentMentions: string[];

  constructor(
    private $scope: IFeedCommentsScope & IExtendedRootScopeService,
    private $stateParams: IFeedCommentsStateParams,
    private $timeout: ITimeoutService,
    private $ngRedux: INgRedux,
    private $q: IQService,
    private feedActions: FeedActions,
    private metricService: MetricService,
    private goalService: GoalService,
    private currentUserService: CurrentUserService,
    private commentService: CommentService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);

    this.entityOwnersSet = new Set<string>();
    this.mentionsSet = new Set<string>();
    this.currentMentions = [];

    this.$scope.mentions = [];
    this.$scope.metadata = {};

    this.$scope.params = { ...this.$stateParams };
    this.$scope.isCurrentUserViewOnly = this.currentUserService.userHasRole(USER_ROLES_VIEWONLY);

    this.$scope.$on("commentEdited", (event, comment) => this.onCommentEdited(comment));
    this.$scope.$on("commentDeleted", (event, comment) => this.onCommentDeleted(comment));

    this.$scope.getTargetTypeForComments = getTargetTypeForComments;
    this.$scope.setCurrentCommentMentions = this.setCurrentCommentMentions;
    this.$scope.onCommentPosted = this.onCommentPosted;
    this.$scope.onCommentEdited = this.onCommentEdited;
    this.$scope.createComment = this.createComment;
    this.$scope.updateFeedEntryComments = this.updateFeedEntryComments;
    this.$scope.commentsSortingChanged = this.commentsSortingChanged;

    this.$scope.handleEditorOpen = (commentId: string) => (this.$scope.editedCommentId = commentId);

    this.$scope.$watch("feedEntry", () => {
      this.updateMentions();
    });

    this.$scope.$on("$destroy", () => {
      unsubscribe();
      this.$ngRedux.dispatch(this.feedActions.clearSelectFeedEntry());
    });
  }

  public stateInit(): IPromise<unknown> {
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.feedFetched,
        () => this.$scope.reduxItemsError
      )
      .then(() => {
        this.$ngRedux.dispatch(this.feedActions.selectFeedEntryById(this.$scope.params.targetId));
        this.setEntityOwners().then(() => {
          this.updateMentions();
          this.$scope.metadata = { metricId: this.$scope.feedEntry.item.id };
        });
      });
  }

  public createComment = (args: { text: string; gif: Gif; mentions: string[]; editor: RichTextEditorComponent }): void => {
    this.$scope.editorButtonsDisabled = true;
    this.$scope.$evalAsync();

    this.commentService
      .post(
        {
          targetId: this.$scope.feedEntry.targetId,
          targetType: this.$scope.getTargetTypeForComments(this.$scope.feedEntry.type),
          targetTitle: this.$scope.feedEntry.item.name,
          text: args.text,
          mentioned: args.mentions,
          gif: args.gif,
        } as Comment,
        this.$scope.metadata
      )
      .then((res) => {
        this.onCommentPosted(res);
        args.editor.clearContent();
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(createErrorObject(error));
      })
      .finally(() => {
        this.$scope.editorButtonsDisabled = false;
        this.$scope.$evalAsync();
      });
  };

  public onCommentPosted = (comment: Comment): void => {
    this.$scope.editedCommentId = null;

    // Two delays since events need a bit to propagate and get processed
    const firstCheckForNewComment = this.$timeout(() => {
      this.$ngRedux.dispatch(this.feedActions.fetchAndSelectFeedEntryById(this.$scope.params.targetId));
    }, delay);

    const secondCheckForNewComment = this.$timeout(() => {
      const ids = this.$scope.feedEntry.comments.map((c) => c.targetId);
      if (!ids.includes(comment.id)) {
        this.$ngRedux.dispatch(this.feedActions.fetchAndSelectFeedEntryById(this.$scope.params.targetId));
      }
    }, 3 * delay);

    this.$scope.$on("$destroy", () => {
      this.$timeout.cancel(firstCheckForNewComment);
      this.$timeout.cancel(secondCheckForNewComment);
    });
  };

  private onCommentEdited = (comment: Comment): void => {
    this.$scope.editedCommentId = null;
    const oldComment = this.$scope.feedEntry.comments.find((listComment) => listComment.targetId === comment.id);
    if (oldComment) {
      // feed comments are duplicated in the respective feed entry document with a new ID
      // so don't copy the entire object with id, targetId, etc. - take only the updated content values
      oldComment.text = comment.text;
      oldComment.mentioned = comment.mentioned;
      oldComment.gif = comment.gif;
      oldComment.editedBy = comment.editedBy;
    }
    this.updateMentions();
    this.$scope.$evalAsync();
  };

  private onCommentDeleted = (comment: IFeedEntryComment): void => {
    this.$scope.feedEntry.comments = this.$scope.feedEntry.comments.filter((listComment) => listComment.id !== comment.id);
    this.updateMentions();
  };

  private setEntityOwners(): IPromise<void> {
    const feedEntry: IFeedEntry = this.$scope.feedEntry;

    this.entityOwnersSet.add(feedEntry.ownerId);

    if (feedEntry.type === IFeedEntryTypeEnum.KR_UPDATE) {
      return this.metricService.getMetric(feedEntry.item.id).then((metric) => {
        return this.goalService.getGoal(metric.goalId).then((goal) => {
          metric.ownerIds.forEach((id) => this.entityOwnersSet.add(id));
          goal.ownerIds.forEach((id) => this.entityOwnersSet.add(id));
        });
      });
    } else if (feedEntry.type === IFeedEntryTypeEnum.BADGE_ASSIGN) {
      (feedEntry.item as IFeedBadgeAssignItem).assigneeIds.forEach((id) => this.entityOwnersSet.add(id));
      return this.$q.resolve();
    }
  }

  private updateMentions(): void {
    const mentionsSet = new Set<string>();

    // add the entity owners to the mentions
    this.entityOwnersSet.forEach((id) => mentionsSet.add(id));

    this.$scope.feedEntry?.comments?.forEach((comment) => {
      // add the participants in the comment thread
      mentionsSet.add(comment.createdBy);

      // add the mentions in each comment
      comment.mentioned.forEach((id) => mentionsSet.add(id));
    });

    // remove the current user from the mentions, so he doesn't get notified
    mentionsSet.delete(getCurrentUserId());

    this.mentionsSet = mentionsSet;
    this.$scope.mentions = [...mentionsSet];
  }

  private setCurrentCommentMentions = (currentMentions: string[] = []): void => {
    // remove the previous set mentions
    if (this.currentMentions.length > 0) {
      this.currentMentions.forEach((id) => this.mentionsSet.delete(id));
    }

    // set the new mentions
    this.currentMentions = currentMentions;
    currentMentions.forEach((id) => this.mentionsSet.add(id));

    // remove the current user from the mentions, so he doesn't get notified
    this.mentionsSet.delete(getCurrentUserId());

    this.$scope.mentions = [...this.mentionsSet];
  };

  private updateFeedEntryComments = (comments: Comment[]): void => {
    // feed comments use as target ID the original comment ID; required by the reactions component for assigning reactions to the correct entity
    const feedComments = comments?.map((comment) => ({ ...comment, targetId: comment.id }));
    this.$ngRedux.dispatch(this.feedActions.updateFeedEntryComments(this.$scope.feedEntry.id, feedComments));
  };

  private commentsSortingChanged = ($event: CommentsOrdering): void => {
    this.$scope.commentsOrdering = $event;
    this.$scope.$evalAsync();
  };
}
